import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { assetService } from "../services/assetService";
import { systemService } from "../services/systemService";
import { partnerService, Partner } from "../services/partnerService";
import { partnerSatService } from "../services/partnerSatService";

import ErrorModal from "../components/modals/ErrorModal";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [partner, setPartner] = useState<Partner | undefined>(undefined);
  const [token, setToken] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isNotConfirm, setIsNotConfirm] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);

  useEffect(() => {
    const initialize = async () => {
      const stop = await onMountedPartnerUx();
      if (stop) return;
    };

    initialize();
  }, [navigate]);

  useEffect(() => {
    onMountedPartnerUi();
  }, [partner]);

  const onMountedPartnerUx = async (): Promise<boolean> => {
    const initializedPartner = await partnerService.initPartner(
      window.location
    );
    initializedPartner.name = "SAT";
    partnerService.setPartner(initializedPartner);
    setPartner(initializedPartner);

    const extractedToken = partnerSatService.onResetPasswordMounted(
      window.location
    );
    if (!extractedToken) {
      navigate("/");
      return true;
    }

    setToken(extractedToken);
    return false;
  };

  const onMountedPartnerUi = () => {
    if (partner) {
      // Set logo
      const partnerLogo = assetService.getLogo(partner.name);
      setLogo(partnerLogo);
    }
  };

  const reset = async () => {
    if (
      newPassword !== confirmPassword ||
      newPassword === "" ||
      confirmPassword === ""
    ) {
      setIsNotConfirm(true);
      return;
    } else {
      setIsNotConfirm(false);
    }

    const isResetSuccess = await partnerSatService.resetPassword(
      token,
      newPassword
    );

    if (isResetSuccess) {
      setShowSuccessModal(true);
    } else {
      setShowErrorModal(true);
    }
  };

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    navigate("/");
  };

  const handleErrorConfirm = () => {
    setShowErrorModal(false);
  };

  return (
    <div className="flex flex-col items-center p-6 gap-y-4 justify-center min-h-screen w-full">
      <div className="w-full max-w-3xl border p-6 rounded-3xl shadow-md bg-white flex flex-col items-center overflow-hidden mx-auto">
        {/* Logo Section */}
        <div className="logo_container flex justify-center mb-6">
          {logo && <img src={logo} alt="Logo" width={172} height={40} />}
        </div>

        {/* Header */}
        <div className="header text-center mb-6">
          <p className="text-2xl font-semibold">
            {t("pages.resetPassword.title")}
          </p>
        </div>

        {/* New Password Input */}
        <div className="form-group text-center mt-6">
          <label className="block text-lg mb-3">
            {t("pages.resetPassword.message1")}
          </label>
          <div className="w-3/4 mx-auto">
            <input
              className="form-control w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              type="password"
              placeholder="Enter new password"
            />
          </div>
        </div>

        {/* Confirm Password Input */}
        <div className="form-group text-center mt-6">
          <label className="block text-lg mb-3">
            {t("pages.resetPassword.message2")}
          </label>
          <div className="w-3/4 mx-auto">
            <input
              className="form-control w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              type="password"
              placeholder="Confirm new password"
            />
          </div>
        </div>

        {/* Error Message */}
        {isNotConfirm && (
          <div className="text-center mt-4">
            <span className="text-red-500 text-sm">
              {t("pages.resetPassword.message3")}
            </span>
          </div>
        )}

        {/* Submit Button */}
        <div className="text-center mt-6">
          <button
            id="submit"
            className="gold-submit-btn px-8 py-3 text-lg w-[150px]"
            onClick={reset}
          >
            {t("pages.resetPassword.message4")}
          </button>
        </div>
      </div>

      {/* Success Modal */}
      <ErrorModal
        isVisible={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onConfirm={handleSuccessConfirm}
        message={t("pages.resetPassword.successMessage")}
        title={t("pages.resetPassword.successTitle")}
        confirmButtonText={t("pages.resetPassword.confirmButtonText")}
        isCancelable={false}
      />

      {/* Error Modal */}
      <ErrorModal
        isVisible={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onConfirm={handleErrorConfirm}
        message={t("pages.resetPassword.errorMessage")}
        title={t("pages.resetPassword.errorTitle")}
        confirmButtonText={t("pages.resetPassword.errorConfirmButtonText")}
        isCancelable={false}
      />
    </div>
  );
}
